import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import agentApiService from '../../services/agentApiService';

const SupportTicket = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');

  // Form state for creating new ticket
  const [formData, setFormData] = useState({
    queryType: 'BOOKING',
    query: '',
    bookingId: '',
    cancellationId: ''
  });

  useEffect(() => {
    console.log('🔍 [SupportTicket] Component mounted, fetching data...');
    fetchTickets();
    fetchStats();
  }, [currentPage, pageSize]);

  useEffect(() => {
    // Filter tickets based on search term and status
    let filtered = tickets;
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(ticket =>
        ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentQuery?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.queryType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ticketStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== '') {
      filtered = filtered.filter(ticket => 
        ticket.ticketStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    
    setFilteredTickets(filtered);
  }, [tickets, searchTerm, statusFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      console.log('🔍 [SupportTicket] Fetching tickets from API...');
      
      const response = await agentApiService.getSupportTickets(currentPage, pageSize);
      console.log('📊 [SupportTicket] API Response:', response);
      
      if (response.success) {
        setTickets(response.content || []);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
        setHasNext(response.hasNext || false);
        setHasPrevious(response.hasPrevious || false);
        
        console.log('✅ [SupportTicket] Data loaded successfully:', {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          contentLength: response.content?.length || 0
        });
      } else {
        console.error('❌ [SupportTicket] API returned error:', response.message);
        setError(response.message || 'Failed to fetch support tickets');
        toast.error(response.message || 'Failed to fetch support tickets');
      }
    } catch (error) {
      console.error('❌ [SupportTicket] Network/API error:', error);
      setError(error.message);
      toast.error('Failed to fetch support tickets: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('📊 [SupportTicket] Fetching stats from API...');
      const response = await agentApiService.getSupportTicketStats();
      console.log('📊 [SupportTicket] Stats Response:', response);
      
      if (response.success) {
        setStats(response.stats);
        console.log('✅ [SupportTicket] Stats loaded successfully:', response.stats);
      } else {
        console.warn('⚠️ [SupportTicket] Stats API returned error:', response.message);
      }
    } catch (error) {
      console.error('❌ [SupportTicket] Stats API error:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!formData.queryType || !formData.query) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      console.log('🎫 [SupportTicket] Creating support ticket:', formData);
      
      const response = await agentApiService.createSupportTicket(formData);
      console.log('✅ [SupportTicket] Ticket created:', response);
      
      if (response.success) {
        toast.success('Support ticket created successfully!');
        setShowCreateModal(false);
        setFormData({
          queryType: 'BOOKING',
          query: '',
          bookingId: '',
          cancellationId: ''
        });
        fetchTickets(); // Refresh the list
        fetchStats(); // Refresh stats
      } else {
        toast.error(response.message || 'Failed to create support ticket');
      }
    } catch (error) {
      console.error('❌ [SupportTicket] Error creating ticket:', error);
      toast.error('Failed to create support ticket: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/agent/dashboard/support-ticket/${ticketId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(0); // Reset to first page
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'assigned':
        return 'bg-purple-100 text-purple-800';
      case 'inprogress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolve':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'fas fa-ticket-alt text-blue-600';
      case 'assigned':
        return 'fas fa-user-check text-purple-600';
      case 'inprogress':
        return 'fas fa-spinner text-yellow-600';
      case 'resolve':
        return 'fas fa-check-circle text-green-600';
      default:
        return 'fas fa-question-circle text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Support Tickets</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Tickets</h1>
        <p className="text-gray-600">Create and manage your support tickets for assistance</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-blue-600">{stats.totalTickets}</div>
            <div className="text-sm text-gray-600">Total Tickets</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-blue-600">{stats.openTickets}</div>
            <div className="text-sm text-gray-600">Open</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-purple-600">{stats.assignedTickets}</div>
            <div className="text-sm text-gray-600">Assigned</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgressTickets}</div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-green-600">{stats.resolvedTickets}</div>
            <div className="text-sm text-gray-600">Resolved</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-2xl font-bold text-red-600">{stats.unreadTickets}</div>
            <div className="text-sm text-gray-600">Unread</div>
          </div>
        </div>
      )}

      {/* Search and Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by ticket ID, query, type, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="assigned">Assigned</option>
                <option value="inprogress">In Progress</option>
                <option value="resolve">Resolved</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-300"
            >
              <i className="fas fa-plus mr-2"></i>
              Add Ticket
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Support Tickets</h2>
        </div>
        
        {filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">
              <i className="fas fa-ticket-alt"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Support Tickets</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter ? 
                'No tickets match your current filters.' : 
                'You haven\'t created any support tickets yet.'}
            </p>
            {!searchTerm && !statusFilter && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-300"
              >
                Create Your First Ticket
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Query Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Query
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTickets.map((ticket, index) => (
                    <tr key={ticket.ticketId || index} className={`hover:bg-gray-50 ${!ticket.viewed ? 'bg-blue-50 font-semibold' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <i className={`${getStatusIcon(ticket.ticketStatus)} mr-2`}></i>
                          <div className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800"
                               onClick={() => handleTicketClick(ticket.ticketId)}>
                            {ticket.ticketId || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          {ticket.queryType || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {ticket.agentQuery || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(ticket.ticketStatus)}`}>
                          {ticket.ticketStatus || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(ticket.createdDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleTicketClick(ticket.ticketId)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
              {filteredTickets.map((ticket, index) => (
                <div key={ticket.ticketId || index} 
                     className={`border-b border-gray-200 p-4 ${!ticket.viewed ? 'bg-blue-50 font-semibold' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      <i className={`${getStatusIcon(ticket.ticketStatus)} mr-2`}></i>
                      <div className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800"
                           onClick={() => handleTicketClick(ticket.ticketId)}>
                        {ticket.ticketId || 'N/A'}
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(ticket.ticketStatus)}`}>
                      {ticket.ticketStatus || 'Unknown'}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">Type:</span> {ticket.queryType || 'N/A'}</div>
                    <div><span className="font-medium">Query:</span> {ticket.agentQuery || 'N/A'}</div>
                    <div><span className="font-medium">Created:</span> {formatDate(ticket.createdDate)}</div>
                  </div>
                  <div className="mt-2">
                    <button
                      onClick={() => handleTicketClick(ticket.ticketId)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPrevious}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min((currentPage + 1) * pageSize, totalElements)}
                    </span>{' '}
                    of <span className="font-medium">{totalElements}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPrevious}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(0, Math.min(currentPage - 2 + i, totalPages - 1));
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Create Support Ticket</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query Type *
                </label>
                <select
                  value={formData.queryType}
                  onChange={(e) => setFormData({...formData, queryType: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="BOOKING">BOOKING</option>
                  <option value="CANCELLATION">CANCELLATION</option>
                  <option value="PAYMENTS">PAYMENTS</option>
                  <option value="EXTRA_TOKEN_REQUEST">Extra Token Request</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query *
                </label>
                <textarea
                  value={formData.query}
                  onChange={(e) => setFormData({...formData, query: e.target.value})}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your issue or question..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.bookingId}
                  onChange={(e) => setFormData({...formData, bookingId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter booking ID if applicable"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.cancellationId}
                  onChange={(e) => setFormData({...formData, cancellationId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter cancellation ID if applicable"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-300"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Creating...
                    </>
                  ) : (
                    'Create Ticket'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicket;